// SharedSkillDetail — /dashboard/platform/shared-skills/:id
// active skill detail + version history + usage metrics + lifecycle.
// Spec §9.4.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft, History, Activity, PowerOff, Power, AlertCircle,
  TrendingUp,
} from "lucide-react";
import PageHeader from "../../components/PageHeader.js";
import Skeleton from "../../components/Skeleton.js";
import {
  getActiveSkill, getSkillHistory, getSkillMetrics, getSkillUsage, getSkillEvaluations,
  deprecateSkill, reactivateSkill, friendlySharedSkillsError,
  DOMAIN_LABEL, RISK_LABEL, ACTIVE_STATUS_LABEL, IMPACT_LABEL, OUTCOME_LABEL,
  type ActiveSkillDetail, type ActiveSkillListItem, type MetricsResponse,
  type UsageLogRow, type EvaluationRow,
} from "../../lib/sharedSkillsApi.js";
import { formatTaipeiDateTime } from "../../../lib/time.js";

type State =
  | { status: "loading" }
  | { status: "ready"; skill: ActiveSkillDetail; history: ActiveSkillListItem[]; metrics: MetricsResponse; usage: UsageLogRow[]; evals: EvaluationRow[] }
  | { status: "error"; message: string };

type ActionPanel =
  | null
  | { kind: "deprecate"; reason: string }
  | { kind: "reactivate"; reason: string };

export default function SharedSkillDetail(): React.ReactElement {
  const { id = "" } = useParams<{ id: string }>();
  const [state, setState] = useState<State>({ status: "loading" });
  const [panel, setPanel] = useState<ActionPanel>(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [window, setWindow] = useState<"7d" | "30d" | "all">("30d");

  const load = useCallback(async () => {
    if (!id) return;
    setState({ status: "loading" });
    try {
      const skill = await getActiveSkill(id);
      const [history, metrics, usage, evals] = await Promise.all([
        getSkillHistory(skill.public_id).then((r) => r.items),
        getSkillMetrics(id, window),
        getSkillUsage(id, { limit: 20 }).then((r) => r.items),
        getSkillEvaluations(id, { limit: 20 }).then((r) => r.items),
      ]);
      setState({ status: "ready", skill, history, metrics, usage, evals });
    } catch (e) {
      setState({ status: "error", message: friendlySharedSkillsError(e) });
    }
  }, [id, window]);

  useEffect(() => { void load(); }, [load]);

  const requiredChecks: string[] = useMemo(() => safeJsonArray(state.status === "ready" ? state.skill.required_checks : null), [state]);
  const relatedTools: string[] = useMemo(() => safeJsonArray(state.status === "ready" ? state.skill.related_tools : null), [state]);

  const doDeprecate = async () => {
    if (panel?.kind !== "deprecate") return;
    if (!panel.reason.trim()) { setToast("請填寫停用原因"); return; }
    setBusy(true);
    try {
      await deprecateSkill(id, panel.reason.trim());
      setToast("已停用");
      setPanel(null);
      void load();
    } catch (e) { setToast(friendlySharedSkillsError(e)); }
    finally { setBusy(false); }
  };

  const doReactivate = async () => {
    if (panel?.kind !== "reactivate") return;
    if (!panel.reason.trim()) { setToast("請填寫重啟原因"); return; }
    setBusy(true);
    try {
      await reactivateSkill(id, panel.reason.trim());
      setToast("已重新啟用");
      setPanel(null);
      void load();
    } catch (e) { setToast(friendlySharedSkillsError(e)); }
    finally { setBusy(false); }
  };

  if (state.status === "loading") {
    return <div className="space-y-3"><Skeleton className="h-10 w-1/2" /><Skeleton className="h-32 w-full" /><Skeleton className="h-40 w-full" /></div>;
  }
  if (state.status === "error") {
    return (
      <div className="space-y-3">
        <Link to="/dashboard/platform/shared-skills" className="text-xs text-text-muted hover:text-text-primary inline-flex items-center gap-1"><ArrowLeft className="h-3 w-3" /> 返回</Link>
        <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-4 text-sm text-red-300 flex items-center gap-2"><AlertCircle className="h-4 w-4" />{state.message}</div>
      </div>
    );
  }

  const s = state.skill;
  const canDeprecate = s.status === "active";
  const canReactivate = s.status === "deprecated" && s.superseded_by === null;

  return (
    <div className="space-y-4 max-w-4xl">
      <PageHeader
        title={s.title}
        description={`${s.public_id} · v${s.version} · ${ACTIVE_STATUS_LABEL[s.status]}`}
        actions={
          <Link
            to="/dashboard/platform/shared-skills"
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-bg-surface hover:bg-bg-inset px-3 py-1.5 text-xs"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> 返回技能庫
          </Link>
        }
      />

      {/* Metadata */}
      <div className="rounded-xl border border-border bg-bg-surface p-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
        <Cell label="狀態" value={ACTIVE_STATUS_LABEL[s.status]} />
        <Cell label="version" value={s.version} />
        <Cell label="domain" value={DOMAIN_LABEL[s.domain]} />
        <Cell label="風險" value={RISK_LABEL[s.risk_level]} />
        <Cell label="priority" value={s.priority} />
        <Cell label="來源" value={s.source_type} />
        <Cell label="批准於" value={formatTaipeiDateTime(s.approved_at)} />
        {s.deprecated_at && <Cell label="停用於" value={formatTaipeiDateTime(s.deprecated_at)} />}
      </div>

      {/* Structured content */}
      <Section title="技能內容">
        <p className="text-xs text-text-secondary whitespace-pre-wrap mb-3">{s.summary}</p>
        <Field label="applicable_scenarios" body={s.applicable_scenarios} />
        <Field label="trigger_conditions" body={s.trigger_conditions} />
        <Field label="recommended_behavior" body={s.recommended_behavior} />
        <Field label="forbidden_behavior" body={s.forbidden_behavior} />
        <Field label="required_checks" body={requiredChecks.join("、") || "（無）"} />
        <Field label="related_tools" body={relatedTools.join("、") || "（無）"} />
      </Section>

      {/* Metrics */}
      <Section
        title="使用統計"
        right={
          <select value={window} onChange={(e) => setWindow(e.target.value as typeof window)}
            className="rounded-md bg-bg-inset border border-border text-2xs px-2 py-1">
            <option value="7d">近 7 天</option>
            <option value="30d">近 30 天</option>
            <option value="all">全部</option>
          </select>
        }
      >
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
          <Cell label="總檢索次數" value={state.metrics.usage.total} />
          <Cell label="影響的不同 tenant 數" value={state.metrics.usage.distinct_tenants} />
          <Cell label="evaluation 記錄數" value={state.metrics.evaluations.total} />
        </div>
        {Object.keys(state.metrics.usage.by_impact_type).length > 0 && (
          <div className="mt-3">
            <div className="text-2xs text-text-muted mb-1">impact 分布</div>
            <div className="flex flex-wrap gap-2 text-2xs">
              {Object.entries(state.metrics.usage.by_impact_type).map(([k, v]) => (
                <span key={k} className="rounded-md bg-bg-inset border border-border px-2 py-0.5">
                  {IMPACT_LABEL[k as keyof typeof IMPACT_LABEL] ?? k}: {v}
                </span>
              ))}
            </div>
          </div>
        )}
        {Object.keys(state.metrics.evaluations.by_outcome).length > 0 && (
          <div className="mt-3">
            <div className="text-2xs text-text-muted mb-1">outcome 分布</div>
            <div className="flex flex-wrap gap-2 text-2xs">
              {Object.entries(state.metrics.evaluations.by_outcome).map(([k, v]) => (
                <span key={k} className="rounded-md bg-bg-inset border border-border px-2 py-0.5">
                  {OUTCOME_LABEL[k as keyof typeof OUTCOME_LABEL] ?? k}: {v}
                </span>
              ))}
            </div>
          </div>
        )}
      </Section>

      {/* Version history */}
      {state.history.length > 1 && (
        <Section title={<span className="inline-flex items-center gap-1.5"><History className="h-3.5 w-3.5" /> 版本歷史</span>}>
          <ul className="text-xs space-y-1.5">
            {state.history.map((h) => (
              <li key={h.id} className="flex items-center justify-between gap-3 border-b border-border last:border-0 pb-1.5">
                <Link to={`/dashboard/platform/shared-skills/${h.id}`} className={`hover:text-accent ${h.id === s.id ? "text-accent font-semibold" : "text-text-primary"}`}>
                  v{h.version} · {ACTIVE_STATUS_LABEL[h.status]} {h.superseded_by ? "（已被取代）" : ""}
                </Link>
                <span className="text-2xs text-text-muted whitespace-nowrap">{formatTaipeiDateTime(h.approved_at)}</span>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* Recent usage */}
      {state.usage.length > 0 && (
        <Section title={<span className="inline-flex items-center gap-1.5"><Activity className="h-3.5 w-3.5" /> 最近檢索紀錄</span>}>
          <ul className="text-xs space-y-1">
            {state.usage.map((u) => (
              <li key={u.id} className="flex items-center justify-between gap-3 border-b border-border last:border-0 pb-1">
                <div>
                  <span className="text-text-primary">{IMPACT_LABEL[u.impact_type] ?? u.impact_type}</span>
                  <span className="text-2xs text-text-muted ml-2">{u.matched_reason}</span>
                  {u.match_score !== null && <span className="text-2xs text-text-muted ml-2">score: {u.match_score.toFixed(3)}</span>}
                </div>
                <span className="text-2xs text-text-muted">{formatTaipeiDateTime(u.created_at)}</span>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* Recent evaluations */}
      {state.evals.length > 0 && (
        <Section title={<span className="inline-flex items-center gap-1.5"><TrendingUp className="h-3.5 w-3.5" /> 最近 evaluation</span>}>
          <ul className="text-xs space-y-1">
            {state.evals.map((e) => (
              <li key={e.id} className="flex items-center justify-between gap-3 border-b border-border last:border-0 pb-1">
                <span className="text-text-primary">{OUTCOME_LABEL[e.outcome] ?? e.outcome}</span>
                <span className="text-2xs text-text-muted">{formatTaipeiDateTime(e.created_at)}</span>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* Action bar */}
      <div className="rounded-xl border border-border bg-bg-surface p-4 space-y-3">
        {!panel && (canDeprecate || canReactivate) && (
          <div className="flex flex-wrap gap-2">
            {canDeprecate && (
              <button onClick={() => setPanel({ kind: "deprecate", reason: "" })}
                className="inline-flex items-center gap-1.5 rounded-md bg-zinc-500/15 hover:bg-zinc-500/25 text-zinc-300 px-3 py-1.5 text-xs">
                <PowerOff className="h-3.5 w-3.5" /> 停用此版本
              </button>
            )}
            {canReactivate && (
              <button onClick={() => setPanel({ kind: "reactivate", reason: "" })}
                className="inline-flex items-center gap-1.5 rounded-md bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 px-3 py-1.5 text-xs">
                <Power className="h-3.5 w-3.5" /> 重新啟用
              </button>
            )}
          </div>
        )}
        {!canDeprecate && !canReactivate && !panel && (
          <p className="text-2xs text-text-muted">
            {s.superseded_by ? "此版本已被新版本取代，無法重啟。請改去最新版本操作。" : "目前無可用動作。"}
          </p>
        )}
        {panel && (
          <ActionForm
            label={panel.kind === "deprecate" ? "停用原因（必填）" : "重啟原因（必填）"}
            value={panel.reason}
            onChange={(v) => setPanel(panel.kind === "deprecate" ? { kind: "deprecate", reason: v } : { kind: "reactivate", reason: v })}
            onConfirm={panel.kind === "deprecate" ? doDeprecate : doReactivate}
            onCancel={() => setPanel(null)}
            busy={busy}
            confirmLabel={panel.kind === "deprecate" ? "確認停用" : "確認重啟"}
            danger={panel.kind === "deprecate"}
          />
        )}
      </div>

      {toast && (
        <div className="fixed bottom-4 right-4 max-w-md rounded-md bg-bg-elevated border border-border px-3 py-2 text-xs shadow-2xl">
          {toast}
          <button onClick={() => setToast(null)} className="ml-2 text-text-muted hover:text-text-primary">×</button>
        </div>
      )}
    </div>
  );
}

function Section({ title, right, children }: { title: React.ReactNode; right?: React.ReactNode; children: React.ReactNode }): React.ReactElement {
  return (
    <section className="rounded-xl border border-border bg-bg-surface p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-semibold text-text-primary uppercase tracking-wide">{title}</h3>
        {right}
      </div>
      {children}
    </section>
  );
}

function Cell({ label, value }: { label: string; value: string | number }): React.ReactElement {
  return (
    <div>
      <div className="text-2xs text-text-muted">{label}</div>
      <div className="mt-0.5 text-text-primary font-medium">{value}</div>
    </div>
  );
}

function Field({ label, body }: { label: string; body: string }): React.ReactElement {
  return (
    <div className="border-b border-border last:border-0 pb-2 mb-2 last:mb-0">
      <div className="text-2xs font-mono text-text-muted">{label}</div>
      <p className="mt-0.5 text-xs text-text-secondary whitespace-pre-wrap leading-relaxed">{body}</p>
    </div>
  );
}

function ActionForm(props: { label: string; value: string; onChange: (v: string) => void; onConfirm: () => void; onCancel: () => void; busy: boolean; confirmLabel: string; danger?: boolean }): React.ReactElement {
  return (
    <div className="space-y-2">
      <input type="text" placeholder={props.label} value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        className="w-full rounded-md bg-bg-inset border border-border text-xs px-3 py-1.5"
      />
      <div className="flex gap-2">
        <button onClick={props.onConfirm} disabled={props.busy}
          className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold disabled:opacity-50 ${
            props.danger ? "bg-red-500 hover:bg-red-400 text-bg-inset" : "bg-emerald-500 hover:bg-emerald-400 text-bg-inset"
          }`}>
          {props.busy ? "處理中…" : props.confirmLabel}
        </button>
        <button onClick={props.onCancel} disabled={props.busy} className="rounded-md border border-border bg-bg-inset px-3 py-1.5 text-xs">取消</button>
      </div>
    </div>
  );
}

function safeJsonArray(s: string | null): string[] {
  if (!s) return [];
  try { const x = JSON.parse(s); return Array.isArray(x) ? x.filter((v): v is string => typeof v === "string") : []; }
  catch { return []; }
}
